import { createReducer } from './utils';
import { translate as t } from '@/core/i18n/translate';
import { ChecklistResponseBatchInput } from '@shared/schema';
import {
  GET_CHECKLISTS_REQUEST,
  GET_CHECKLISTS_SUCCESS,
  GET_CHECKLISTS_FAILURE,
  SNACK_SET,
  SAVE_CHECKLIST_RESPONSES_REQUEST,
  SAVE_CHECKLIST_RESPONSES_SUCCESS,
  SAVE_CHECKLIST_RESPONSES_FAILURE,
  CHECKLIST_RESPONSES_REQUEST,
  CHECKLIST_RESPONSES_SUCCESS,
  CHECKLIST_RESPONSES_FAILURE,
  RESET_CHECKLISTS_STATE,
} from './constants';
import {
  ChecklistsState,
  Action,
  ThunkAPI,
  ChecklistWithRelations,
  ChecklistSavedResponse,
} from '../types/state';

const defaultState: ChecklistsState = {
  checklists: [],
  loading: false,
  loadingResponses: false,
  errorMessage: null,
  responses: [],
};

export const reducer = createReducer(defaultState, {
  [GET_CHECKLISTS_REQUEST]: handleGetChecklistsRequest,
  [GET_CHECKLISTS_SUCCESS]: handleGetChecklistsSuccess,
  [GET_CHECKLISTS_FAILURE]: handleGetChecklistsFailure,
  [SAVE_CHECKLIST_RESPONSES_REQUEST]: handleSaveChecklistResponsesRequest,
  [SAVE_CHECKLIST_RESPONSES_SUCCESS]: handleSaveChecklistResponsesSuccess,
  [SAVE_CHECKLIST_RESPONSES_FAILURE]: handleSaveChecklistResponsesFailure,
  [CHECKLIST_RESPONSES_REQUEST]: handleChecklistResponsesRequest,
  [CHECKLIST_RESPONSES_SUCCESS]: handleChecklistResponsesSet,
  [CHECKLIST_RESPONSES_FAILURE]: handleChecklistResponsesFailure,
  [RESET_CHECKLISTS_STATE]: () => defaultState,
});

function handleGetChecklistsRequest(state: ChecklistsState): ChecklistsState {
  return { ...state, loading: true };
}

function handleGetChecklistsSuccess(
  state: ChecklistsState,
  action: Action<unknown>,
): ChecklistsState {
  const payload = action.payload as { checklists: ChecklistWithRelations[] } | undefined;
  return {
    ...state,
    checklists: payload?.checklists || [],
    loading: false,
    errorMessage: null,
  };
}

function handleGetChecklistsFailure(
  state: ChecklistsState,
  action: Action<unknown>,
): ChecklistsState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || t('apiFeedback.checklists.fetchError'),
  };
}

function handleSaveChecklistResponsesRequest(state: ChecklistsState): ChecklistsState {
  return { ...state, loading: true };
}

function handleSaveChecklistResponsesSuccess(state: ChecklistsState): ChecklistsState {
  return { ...state, loading: false };
}

function handleSaveChecklistResponsesFailure(
  state: ChecklistsState,
  action: Action<unknown>,
): ChecklistsState {
  const payload = action.payload as { errorMessage: string } | undefined;
  return {
    ...state,
    loading: false,
    errorMessage: payload?.errorMessage || t('apiFeedback.checklists.saveError'),
  };
}

function handleChecklistResponsesSet(
  state: ChecklistsState,
  action: Action<unknown>,
): ChecklistsState {
  const payload = action.payload as { responses: ChecklistSavedResponse[] } | undefined;
  return { ...state, responses: payload?.responses || [], loadingResponses: false };
}

function handleChecklistResponsesRequest(state: ChecklistsState): ChecklistsState {
  return { ...state, loadingResponses: true };
}

function handleChecklistResponsesFailure(state: ChecklistsState): ChecklistsState {
  return { ...state, loadingResponses: false };
}

export function fetchChecklistsByVersionId(versionId: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: GET_CHECKLISTS_REQUEST });
    try {
      const checklists = await dataSource.fetchChecklistsByVersionId(versionId);
      dispatch({ type: GET_CHECKLISTS_SUCCESS, payload: { checklists } });
    } catch (error) {
      console.error('Error fetching checklists:', error);
      dispatch({
        type: GET_CHECKLISTS_FAILURE,
        payload: {
          errorMessage:
            error instanceof Error ? error.message : t('apiFeedback.checklists.fetchError'),
        },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.checklists.fetchError'),
        severity: 'error' as const,
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}

export function fetchChecklistResponsesByVersionId(versionId: number) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    try {
      dispatch({ type: 'CHECKLIST_RESPONSES_REQUEST', payload: { responses: [] } });
      const responses = await dataSource.getChecklistResponses(versionId);
      dispatch({ type: 'CHECKLIST_RESPONSES_SUCCESS', payload: { responses } });
    } catch (e) {
      dispatch({ type: 'CHECKLIST_RESPONSES_FAILURE', payload: { responses: [] } });
      console.error('Error fetching checklist responses', e);
      const snack = {
        open: true,
        message: t('apiFeedback.checklists.responsesFetchError'),
        severity: 'error' as const,
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    }
  };
}

export interface ChecklistAnswerToSave {
  questionId: number;
  optionId: number | null;
  comment: string;
  photo: string | null;
}

export interface ChecklistResponseToSave extends ChecklistResponseBatchInput {
  APFulfillmentsVersionId: number;
  checklistId: number;
  APChecklistId?: number | null;
  comment: string;
  isFinished: boolean;
  answers: ChecklistAnswerToSave[];
  customFields: { customFieldId: number; value: number }[];
}

export function resetChecklistState() {
  return (dispatch: ThunkAPI['dispatch']) => {
    dispatch({ type: RESET_CHECKLISTS_STATE });
  };
}

export function saveChecklistResponses(payload: ChecklistResponseToSave[]) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    dispatch({ type: SAVE_CHECKLIST_RESPONSES_REQUEST });
    try {
      await dataSource.saveChecklistResponsesBatch(payload);
      dispatch({ type: SAVE_CHECKLIST_RESPONSES_SUCCESS });
      const snack = {
        open: true,
        message: t('apiFeedback.checklists.saveSuccess'),
        severity: 'success' as const,
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
    } catch (error) {
      console.error('Error saving checklist responses:', error);
      dispatch({
        type: SAVE_CHECKLIST_RESPONSES_FAILURE,
        payload: {
          errorMessage:
            error instanceof Error ? error.message : t('apiFeedback.checklists.saveError'),
        },
      });
      const snack = {
        open: true,
        message: t('apiFeedback.checklists.saveError'),
        severity: 'error' as const,
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      throw error;
    }
  };
}

export function uploadChecklistAnswerEvidenceAction(file: File) {
  return async (
    dispatch: ThunkAPI['dispatch'],
    _getState: ThunkAPI['getState'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    try {
      const result = await dataSource.uploadChecklistAnswerEvidence?.(file);
      return result;
    } catch (error) {
      console.error('Error uploading checklist answer evidence', error);
      const snack = {
        open: true,
        message: t('apiFeedback.checklists.evidenceUploadError'),
        severity: 'error' as const,
      };
      dispatch({ type: SNACK_SET, payload: { snack } });
      return null;
    }
  };
}

export function resolveChecklistAnswerEvidenceAction(name: string) {
  return async (
    _dispatch: ThunkAPI['dispatch'],
    { services: { dataSource } }: Pick<ThunkAPI, 'services'>,
  ) => {
    try {
      const result = await (dataSource as any).resolveChecklistAnswerEvidence?.(name);
      return result as { name: string; url: string } | null;
    } catch (e) {
      console.warn('No se pudo resolver evidencia', name, e);
      return null;
    }
  };
}
